// =============================================================================
// Provider Variant Mappings Section — accordion per-UPT variant SKU management
// =============================================================================

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Input, Button, Spinner, Badge } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';

interface VariantOption {
  type: string;
  value: string;
}

interface VariantMappingData {
  id: string;
  externalSku: string | null;
  externalVariantId: string | null;
  costOverride: string | null;
  isActive: boolean;
}

interface VariantRow {
  blankVariantId: string;
  sku: string;
  supplierSku: string | null;
  options: VariantOption[];
  mapping: VariantMappingData | null;
}

interface Props {
  providerId: string;
  uptId: string;
  uptName: string;
}

export function ProviderVariantMappingsSection({ providerId, uptId, uptName }: Props) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [autoFillPrefix, setAutoFillPrefix] = useState('');
  const [savingRow, setSavingRow] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'providers', providerId, 'variant-mappings', uptId],
    queryFn: () =>
      api.get<{ data: VariantRow[] }>(
        `/api/v1/admin/providers/${providerId}/variant-mappings/${uptId}`,
      ),
    enabled: isOpen,
  });

  const saveMutation = useMutation({
    mutationFn: (mappings: Array<{ blankVariantId: string; externalSku: string | null }>) =>
      api.patch(`/api/v1/admin/providers/${providerId}/variant-mappings/${uptId}`, {
        mappings,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'providers', providerId, 'variant-mappings', uptId],
      });
      setEdits({});
    },
  });

  const rowSaveMutation = useMutation({
    mutationFn: (mapping: { blankVariantId: string; externalSku: string | null }) =>
      api.patch(`/api/v1/admin/providers/${providerId}/variant-mappings/${uptId}`, {
        mappings: [mapping],
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'providers', providerId, 'variant-mappings', uptId],
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[variables.blankVariantId];
        return next;
      });
      setSavingRow(null);
    },
    onError: () => setSavingRow(null),
  });

  const variants = data?.data ?? [];
  const mappedCount = variants.filter((v) => v.mapping?.externalSku).length;

  const getEditValue = useCallback(
    (variantId: string, current: string | null) => {
      return edits[variantId] ?? current ?? '';
    },
    [edits],
  );

  const handleEdit = useCallback((variantId: string, value: string) => {
    setEdits((prev) => ({ ...prev, [variantId]: value }));
  }, []);

  const handleAutoFill = useCallback(() => {
    if (!autoFillPrefix || variants.length === 0) return;
    const next: Record<string, string> = {};
    const hasPlaceholders = /\{OPT\d+\}/.test(autoFillPrefix);

    for (const v of variants) {
      const optParts = v.options.map((o) => o.value.toUpperCase().replace(/\s+/g, '-'));

      if (hasPlaceholders) {
        let sku = autoFillPrefix;
        optParts.forEach((part, i) => {
          sku = sku.replace(`{OPT${i + 1}}`, part);
        });
        next[v.blankVariantId] = sku;
      } else {
        next[v.blankVariantId] = `${autoFillPrefix}-${optParts.join('-')}`;
      }
    }
    setEdits(next);
  }, [autoFillPrefix, variants]);

  const handleSaveAll = useCallback(() => {
    const mappings = variants.map((v) => ({
      blankVariantId: v.blankVariantId,
      externalSku: edits[v.blankVariantId] ?? v.mapping?.externalSku ?? null,
    }));
    saveMutation.mutate(mappings);
  }, [variants, edits, saveMutation]);

  const handleRowSave = useCallback((variantId: string) => {
    const value = edits[variantId];
    if (value === undefined) return;
    setSavingRow(variantId);
    rowSaveMutation.mutate({
      blankVariantId: variantId,
      externalSku: value || null,
    });
  }, [edits, rowSaveMutation]);

  const hasChanges = Object.keys(edits).length > 0;

  return (
    <Card padding="none">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronIcon open={isOpen} />
          <span className="text-sm font-medium text-text-primary">{uptName}</span>
        </div>
        <Badge variant="default" size="sm">
          {mappedCount}/{variants.length} mapped
        </Badge>
      </button>

      {isOpen && (
        <div className="border-t border-border">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              {/* Auto-fill bar */}
              <div className="flex items-end gap-2 px-4 py-3 bg-surface-secondary/20 border-b border-border">
                <div className="flex-1">
                  <Input
                    label="Auto-fill pattern"
                    placeholder="e.g. TKH-{OPT1}-{OPT2} or just a prefix"
                    value={autoFillPrefix}
                    onChange={(e) => setAutoFillPrefix(e.target.value)}
                    hint="Use {OPT1}, {OPT2} for option placeholders, or enter a prefix"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAutoFill}
                  disabled={!autoFillPrefix}
                >
                  Apply Pattern
                </Button>
              </div>

              {/* Variants table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-secondary/30">
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-tertiary">
                        Options
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-tertiary">
                        Internal SKU
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-tertiary">
                        External SKU
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-tertiary w-16">
                        Status
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-text-tertiary w-20">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {variants.map((v) => (
                      <tr key={v.blankVariantId} className="hover:bg-surface-secondary/20">
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {v.options.map((opt) => (
                              <span
                                key={opt.type}
                                className="inline-flex items-center rounded bg-surface-secondary px-1.5 py-0.5 text-[11px] text-text-secondary"
                              >
                                {opt.type}: {opt.value}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                          {v.sku}
                        </td>
                        <td className="px-4 py-2">
                          <Input
                            value={getEditValue(v.blankVariantId, v.mapping?.externalSku ?? null)}
                            onChange={(e) => handleEdit(v.blankVariantId, e.target.value)}
                            placeholder="External SKU"
                          />
                        </td>
                        <td className="px-4 py-2">
                          {v.mapping?.externalSku ? (
                            <Badge variant="success" size="sm">Mapped</Badge>
                          ) : (
                            <Badge variant="default" size="sm">—</Badge>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRowSave(v.blankVariantId)}
                            disabled={edits[v.blankVariantId] === undefined || savingRow === v.blankVariantId}
                            loading={savingRow === v.blankVariantId}
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Save bar */}
              <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
                {hasChanges && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEdits({})}
                  >
                    Discard
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={!hasChanges || saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save All Mappings'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
